'use client';
import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, TrendingDown, Clock, Zap } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// Revenue gap: $19.26M/year unrealised at current 11% registration rate
// $19,260,000 / 365 = $52,767/day
// $19,260,000 / 12 = $1,605,000/month
const ANNUAL_GAP_USD = 19_260_000;
const DAILY_LOSS = ANNUAL_GAP_USD / 365;
const MONTHLY_LOSS = ANNUAL_GAP_USD / 12;
const PER_SECOND = DAILY_LOSS / 86400;
const PER_MINUTE = DAILY_LOSS / 1440;

function useCountUp(target: number, duration = 1200) {
  const [value, setValue] = useState(0);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    startRef.current = null;
    const animate = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  return value;
}

function LiveCounter() {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed((Date.now() - startRef.current) / 1000);
    }, 50);
    return () => clearInterval(id);
  }, []);

  const lostSinceLoad = elapsed * PER_SECOND;

  return (
    <div className="flex flex-col items-center justify-center py-4 px-6 rounded-2xl relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(220,38,38,0.06) 100%)', border: '1px solid rgba(239,68,68,0.25)' }}>
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 0%, rgba(239,68,68,0.08) 0%, transparent 70%)'
      }} />
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#ef4444' }} />
        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#f87171' }}>Live — Revenue Lost This Session</span>
      </div>
      <div className="text-4xl font-black tabular-nums mt-1" style={{
        color: '#ef4444',
        fontVariantNumeric: 'tabular-nums',
        textShadow: '0 0 30px rgba(239,68,68,0.4)',
        letterSpacing: '-0.02em'
      }}>
        ${lostSinceLoad.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
      <p className="text-xs mt-1" style={{ color: 'rgba(248,113,113,0.7)' }}>
        Since you opened this dashboard · ${PER_SECOND.toFixed(4)}/sec
      </p>
    </div>
  );
}

export default function CostOfInactionTicker() {
  const [mounted, setMounted] = useState(false);
  const dailyCount = useCountUp(Math.round(DAILY_LOSS), 1400);
  const monthlyCount = useCountUp(Math.round(MONTHLY_LOSS), 1600);
  const annualCount = useCountUp(ANNUAL_GAP_USD, 1800);

  useEffect(() => { setMounted(true); }, []);

  const stats = [
    {
      label: 'Lost Per Day',
      value: `$${dailyCount.toLocaleString()}`,
      sub: 'At 11% registration rate',
      color: '#f87171',
      bg: 'rgba(239,68,68,0.1)',
      border: 'rgba(239,68,68,0.2)',
      icon: Clock,
    },
    {
      label: 'Lost Per Month',
      value: `$${monthlyCount.toLocaleString()}`,
      sub: 'Uncollected every 30 days',
      color: '#fb923c',
      bg: 'rgba(251,146,60,0.1)',
      border: 'rgba(251,146,60,0.2)',
      icon: TrendingDown,
    },
    {
      label: 'Lost Per Year',
      value: `$${(annualCount / 1_000_000).toFixed(2)}M`,
      sub: 'Annual revenue gap',
      color: '#fbbf24',
      bg: 'rgba(251,191,36,0.1)',
      border: 'rgba(251,191,36,0.2)',
      icon: Zap,
    },
  ];

  return (
    <section className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--dark-border)', background: 'var(--dark-card)' }}>
      {/* Header */}
      <div className="px-5 pt-5 pb-4" style={{
        borderBottom: '1px solid var(--dark-border)',
        background: 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(220,38,38,0.04) 100%)'
      }}>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.15)' }}>
            <AlertTriangle size={16} style={{ color: '#f87171' }} />
          </div>
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#f87171' }}>Cost of Inaction</span>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Every day without this system, Liberia loses revenue</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }}>
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#ef4444' }} />
            LIVE
          </div>
        </div>
        <h2 className="text-lg font-bold mt-2" style={{ color: 'var(--dark-text)' }}>
          The Price of Delay: $19.26M Unrealised Every Year
        </h2>
        <p className="text-xs mt-0.5 max-w-2xl" style={{ color: 'var(--dark-muted)' }}>
          Based on LRA FY2024 data: $5.14M collected vs $24.4M full potential. At the current 11% registration rate, Liberia forfeits $52,767 every single day.
        </p>
      </div>

      <div className="px-5 py-5 space-y-4">
        {/* Live session counter */}
        {mounted && <LiveCounter />}

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {stats.map(({ label, value, sub, color, bg, border, icon: Icon }) => (
            <div key={label} className="rounded-xl px-4 py-4 flex flex-col gap-1"
              style={{ background: bg, border: `1px solid ${border}` }}>
              <div className="flex items-center gap-2">
                <Icon size={14} style={{ color }} />
                <span className="text-xs font-medium" style={{ color: 'var(--dark-muted)' }}>{label}</span>
              </div>
              <p className="text-2xl font-black tabular-nums" style={{ color, letterSpacing: '-0.02em' }}>{value}</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{sub}</p>
            </div>
          ))}
        </div>

        {/* Urgency bar */}
        <div className="rounded-xl px-4 py-4" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>Revenue Realisation Rate — Current vs Required</span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171' }}>21% of potential</span>
          </div>
          <div className="w-full h-5 rounded-full overflow-hidden relative" style={{ background: 'rgba(239,68,68,0.12)' }}>
            <div className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-1000"
              style={{ width: '21%', background: 'linear-gradient(90deg, #ef4444, #f87171)' }}>
              <span className="text-xs font-bold text-white">21%</span>
            </div>
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-xs" style={{ color: '#f87171' }}>Current: $5.14M/yr</span>
            <span className="text-xs" style={{ color: '#4ade80' }}>Target: $24.4M/yr (100%)</span>
          </div>
          <p className="text-xs mt-3 leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
            <span className="font-semibold" style={{ color: '#fbbf24' }}>Every 1% improvement in registration</span> adds approximately $193,000 in annual revenue.
            Reaching 50% registration alone would generate an additional $9.6M per year.
          </p>
        </div>
      </div>
    </section>
  );
}
